import {
  model,
  Model,
  prop,
  idProp,
  rootRef,
  detach,
  modelAction,
  findParent,
  modelFlow,
  _async,
} from "mobx-keystone";
import { Sponsorship } from "./Sponsorship";
import { sendToBobController } from "../../helpers/requests";

const TIKTOK_POST_RE = /\/(?:video|photo)\/(\d+)/;
const DIRECT_TIKTOK_POST_MESSAGE =
  "Could not resolve TikTok link. Please use a direct post URL like https://www.tiktok.com/@username/video/1234567890 or https://www.tiktok.com/@username/photo/1234567890";

function parseTikTokUrl(raw: string): {
  valid: boolean;
  cleanUrl: string | null;
  hasVideoId: boolean;
  error: string | null;
} {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return { valid: false, cleanUrl: null, hasVideoId: false, error: null };
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return {
      valid: false,
      cleanUrl: null,
      hasVideoId: false,
      error: "Please enter a valid TikTok post URL.",
    };
  }

  if (url.protocol !== "https:") {
    return {
      valid: false,
      cleanUrl: null,
      hasVideoId: false,
      error: "URL must start with https://",
    };
  }

  if (!url.hostname.endsWith("tiktok.com")) {
    return {
      valid: false,
      cleanUrl: null,
      hasVideoId: false,
      error: "URL must be a tiktok.com link.",
    };
  }

  // Strip query params and fragment
  url.search = "";
  url.hash = "";
  const cleanUrl = url.toString();

  const postMatch = cleanUrl.match(TIKTOK_POST_RE);
  if (postMatch) {
    return { valid: true, cleanUrl, hasVideoId: true, error: null };
  }

  // It's a TikTok link but without a video ID (e.g. short link)
  return { valid: true, cleanUrl, hasVideoId: false, error: null };
}

@model("exrails/BobSponsorshipVideoProposal")
export class BobSponsorshipVideoProposal extends Model({
  _version: prop(0),
  id: idProp,
  proposedUrl: prop<string>(""),
  resolvedUrl: prop<string | null>(null),
  userFacingUrlErrorMessage: prop<string | null>(null),
  isResolving: prop<boolean>(false),
  serverErrorMessage: prop<string | null>(null),
  serverSuccessMessage: prop<string | null>(null),
}) {
  @modelAction
  setProposedUrl(raw: string) {
    if (raw === "") {
      this.proposedUrl = "";
      this.resolvedUrl = null;
      this.userFacingUrlErrorMessage = null;
      this.serverErrorMessage = null;
      this.serverSuccessMessage = null;
      return;
    }

    this.proposedUrl = raw;
    this.serverErrorMessage = null;
    this.serverSuccessMessage = null;

    const parsed = parseTikTokUrl(raw);

    if (!parsed.valid) {
      this.userFacingUrlErrorMessage = parsed.error;
      this.resolvedUrl = null;
      return;
    }

    this.userFacingUrlErrorMessage = null;

    if (parsed.hasVideoId) {
      this.resolvedUrl = parsed.cleanUrl;
    } else {
      // Short link — need to resolve
      this.resolvedUrl = null;
      this.resolveShortUrl(parsed.cleanUrl);
    }
  }

  get isUrlReady(): boolean {
    return this.resolvedUrl !== null;
  }

  @modelAction
  setResolvedUrl(url: string | null) {
    this.resolvedUrl = url;
  }

  @modelAction
  setIsResolving(value: boolean) {
    this.isResolving = value;
  }

  @modelAction
  setUserFacingUrlErrorMessage(message: string | null) {
    this.userFacingUrlErrorMessage = message;
  }

  @modelAction
  setServerError(message: string) {
    this.serverErrorMessage = message;
    this.serverSuccessMessage = null;
  }

  @modelAction
  setServerSuccess(message: string) {
    this.serverSuccessMessage = message;
    this.serverErrorMessage = null;
  }

  resolveShortUrl(url: string) {
    this.setIsResolving(true);
    this.setUserFacingUrlErrorMessage(null);

    sendToBobController(
      { bobUuid: this.sponsorship.bob.uuid, url },
      "/bob_sponsorship_video/resolve_url",
    )
      .then((response: any) => {
        if (response.success && response.resolvedUrl) {
          this.setResolvedUrl(response.resolvedUrl);
        } else {
          this.setUserFacingUrlErrorMessage(
            response.message || DIRECT_TIKTOK_POST_MESSAGE,
          );
        }
        this.setIsResolving(false);
      })
      .catch(() => {
        this.setIsResolving(false);
        this.setUserFacingUrlErrorMessage(DIRECT_TIKTOK_POST_MESSAGE);
      });
  }

  @modelFlow
  addVideo = _async(function* (this: BobSponsorshipVideoProposal) {
    const sponsorship = this.sponsorship;
    sponsorship.bob.setAreWeWaitingOnRails(true);
    this.serverErrorMessage = null;
    this.serverSuccessMessage = null;
    const message = {
      bobUuid: sponsorship.bob.uuid,
      videoUrl: this.resolvedUrl,
    };
    sendToBobController(message, "/bob_sponsorship_video/add_video")
      .then((response) => {
        sponsorship.bob.setAreWeWaitingOnRails(false);
        if (response.success === false) {
          this.setServerError(response.message);
          return;
        }
        if (response.sponsorship) {
          sponsorship.setVideos(response.sponsorship.videos || []);
          sponsorship.setHasReachedMaximumViews(
            response.sponsorship.has_reached_maximum_views || false,
          );
          sponsorship.setAvailableRedemptionSatoshis(
            response.sponsorship.available_redemption_satoshis || 0,
          );
          sponsorship.setIsAllowedToScrape(
            response.sponsorship.is_allowed_to_scrape,
          );
          sponsorship.setNextScrapeAllowedAt(
            response.sponsorship.next_scrape_allowed_at,
          );
        }
        sponsorship.deleteAllVideoProposals();
        sponsorship.createAndSelectNewVideoProposal();
      })
      .catch((error) => {
        sponsorship.bob.setAreWeWaitingOnRails(false);
        console.log(error);
      });
  });

  get sponsorship() {
    return findParent<Sponsorship>(
      this,
      (parentNode) => parentNode instanceof Sponsorship,
    );
  }
}

export const BobSponsorshipVideoProposalRef =
  rootRef<BobSponsorshipVideoProposal>(
    "exrails/BobSponsorshipVideoProposalRef",
    {
      onResolvedValueChange(ref, newThing, oldThing) {
        if (newThing && !oldThing) {
          detach(ref);
        }
      },
    },
  );
